import { createSlice, createDraftSafeSelector, PayloadAction } from '@reduxjs/toolkit';

interface IDonationFile {
  regDt: string;
  fileNm: string;
  fileSize: number;
  filePath: string;
  placeId: string;
  fileType: string;
  fileId: string;
  thumbFileSize: number;
  thumbFilePath: string;
  thumbFileHash: number;
}

export interface IDonationItem {
  donationId: string;
  delId?: string | null;
  useYn?: string;
  regId?: string;
  regDt?: string;
  placeId?: string;
  donationDesc?: string;
  modId?: string;
  modDt?: string;
  donationNm?: string;
  delYn?: string;
  donorId?: string | null;
  donorNm?: string | null;
  donationFileList?: IDonationFile[];
}

export interface IDonorItem {
  donorId: string;
  donorNm?: string;
}

interface Pagination {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  limit: number;
  hasNextPage: boolean;
  hasPrevPage: boolean;
}

interface ListSuccessPayload {
  donorList: IDonorItem[];
  pagination: Pagination;
}

interface DonationState {
  donationList: IDonationItem[];
  searchList: IDonorItem[];
  donationInfo?: IDonationItem | null;
  pagination?: Pagination;
  actionResult: string;
  isLoading: boolean;
  error: string | null;
}

const initialState: DonationState = {
  donationList: [],
  searchList: [],
  donationInfo: {},
  actionResult: '',
  isLoading: false,
  error: null,
  pagination: {
    currentPage: 0,
    totalPages: 0,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false,
  },
};

const slice = createSlice({
  name: 'donation',
  initialState,
  reducers: {
    stateClear: state => {
      state.donationList = [];
      state.actionResult = '';
      state.isLoading = false;
      state.error = null;
    },
    list: (state: DonationState) => {
      state.isLoading = true;
      state.actionResult = 'LIST_REQ';
      state.error = null;
    },
    listSuccess: (state: DonationState, { payload }: PayloadAction<ListSuccessPayload>) => {
      state.donationList = payload.donationList;
      state.pagination = payload.pagination;
      state.isLoading = false;
      state.actionResult = 'LIST_OK';
      state.error = null;
    },
    listFailure: (state: DonationState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'LIST_ERR';
      state.error = payload;
    },
    donationOfDonorList: (state: DonationState) => {
      state.actionResult = 'SEARCH_REQ';
      state.error = null;
    },
    donationOfDonorSearchListSuccess: (state: DonationState, { payload }: PayloadAction<IDonorItem[]>) => {
      state.searchList = payload; // 검색용 리스트만 별도 저장
      state.actionResult = 'SEARCH_OK';
      state.error = null;
    },
    donationOfDonorListFailure: (state: DonationState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'SEARCH_ERR';
      state.error = payload;
    },
    add: (state: DonationState, { payload }: PayloadAction<{}>) => {
      state.isLoading = true;
      state.actionResult = 'ADD_REQ';
      state.error = '';
    },
    addSuccess: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.isLoading = false;
      state.actionResult = 'ADD_OK';
      state.error = '';
    },
    addFailure: (state: DonationState, { payload }: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'ADD_ERR';
      state.error = payload;
    },
    detail: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.isLoading = true;
      state.actionResult = 'DETAIL_REQ';
      state.error = '';
    },
    detailSuccess: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.donationInfo = payload.donationInfo;
      state.isLoading = false;
      state.actionResult = 'DETAIL_OK';
      state.error = '';
    },
    detailFailure: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.isLoading = false;
      state.actionResult = 'DETAIL_ERR';
      state.error = payload.error;
    },
    update: (state: DonationState, { payload }: PayloadAction<CsutomerDonationState>) => {
      state.isLoading = true;
      state.actionResult = 'UPDATE_REQ';
      state.error = '';
    },
    updateSuccess: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.isLoading = false;
      state.actionResult = 'UPDATE_OK';
      state.error = '';
    },
    updateFailure: (state: DonationState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'UPDATE_ERR';
      state.error = action.payload;
    },
    remove: (state: DonationState, { payload }: PayloadAction<CsutomerDonationState>) => {
      state.isLoading = true;
      state.actionResult = 'REMOVE_REQ';
      state.error = '';
    },
    removeSuccess: (state: DonationState, { payload }: PayloadAction<DonationState>) => {
      state.isLoading = false;
      state.actionResult = 'REMOVE_OK';
      state.error = '';
    },
    removeFailure: (state: DonationState, action: PayloadAction<string>) => {
      state.isLoading = false;
      state.actionResult = 'REMOVE_ERR';
      state.error = action.payload;
    },
    actionResultClear: (state: DonationState) => {
      state.actionResult = '';
    },
  },
});

// selectors
const selectDonationList = createDraftSafeSelector(
  (state: DonationState) => state.donationList,
  donationList => donationList,
);

const selectDonationInfo = createDraftSafeSelector(
  (state: DonationState) => state.donationInfo,
  donationInfo => donationInfo,
);

const selectPaginationList = createDraftSafeSelector(
  (state: DonationState) => state.pagination,
  pagination => pagination,
);

const selectStatus = createDraftSafeSelector(
  (state: DonationState) => state.actionResult,
  (state: DonationState) => state.isLoading,
  (state: DonationState) => state.error,
  (actionResult, isLoading, error) => ({
    actionResult,
    isLoading,
    error,
  }),
);

export const donationSelector = {
  donationList: (state: any) => selectDonationList(state[DONATION]),
  donationInfo: state => selectDonationInfo(state[DONATION]),
  pagination: (state: any) => selectPaginationList(state[DONATION]),
  status: (state: any) => selectStatus(state[DONATION]),
};

export const DONATION = slice.name;
export const donationReducer = slice.reducer;
export const donationAction = slice.actions;
